import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { closeDialog, displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { DisplayType } from "models/enums/style";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants, FAQConstants } from "utils/constants";
//#endregion
import * as Sentry from "@sentry/react";
//#region components
import { ButtonBase } from "@mui/material";
import { Icon, ActionLinkButton } from "components/common";
import useSupportTicketCreateDialog from "./useCreateDialog";
import useSupportTicketCreateDialogWithFAQ from "./useCreateDialogWithFAQ";
//#endregion
import { SupportTeamContentResult, SupportTeamContent } from "serviceHooks/useSupportTeamContent";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import { useNavigate } from "react-router-dom";

interface SupportTeamBannerProps {
  supportTeamData: SupportTeamContentResult;
}

const SupportTeamBanner: React.FC<SupportTeamBannerProps> = ({ supportTeamData }) => {
  const { data, loading } = supportTeamData;
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const navigate = useNavigate();

  const { getDialog: getSupportTicketCreateDialog } = useSupportTicketCreateDialog();
  const supportTicketDialogWithFAQ = useSupportTicketCreateDialogWithFAQ(undefined, () => dispatch(closeDialog()));

  if (loading || !data?.team || data.team.members.length === 0) return null;

  const { team, content, showPhone } = data;

  /** CMS content with i18n fallback */
  const t = (field: keyof SupportTeamContent, fallbackKey: string) =>
    content?.[field] || LocalizationHelperInstance.getFormattedMessage(fallbackKey);

  const onFAQClick = () => {
    Sentry.captureMessage("Support team: FAQ clicked", { level: "info", tags: { source: "support-banner" } });
    window.open(FAQConstants.FAQUrl, "_blank", "noopener,noreferrer");
  };

  const onSendMessageClick = () => {
    Sentry.captureMessage("Support team: Skicka meddelande clicked", {
      level: "info",
      tags: { source: "support-banner" },
    });
    if (team.skipFaqStep) {
      dispatch(displayDialog(getSupportTicketCreateDialog(undefined, undefined, undefined, true)));
    } else {
      dispatch(displayDialog(supportTicketDialogWithFAQ));
    }
  };

  const onTeamClick = () => {
    Sentry.captureMessage("Support team: Träffa teamet clicked", { level: "info", tags: { source: "support-banner" } });
    navigate(UrlHelper.formatPath("/Support/Team"));
  };

  return (
    <div className={cssExports.supportTeamBanner}>
      {/* Blue box: left text + middle team */}
      <div className={cssExports.supportTeamBannerBlue}>
        <div className={cssExports.supportTeamBannerLeft}>
          <div className={cssExports.supportTeamBannerIcon}>
            <Icon icon={IconDefinitionConstants.WCommentDots} displayType={DisplayType.Info} />
          </div>
          <div className={cssExports.supportTeamBannerTextGroup}>
            <div className={cssExports.supportTeamBannerTitle}>
              {t("bannerTitle", TranslationKeys.Common.HelpTitle)}
            </div>
            <div className={cssExports.supportTeamBannerDescription}>
              {t("bannerDescription", TranslationKeys.Common.HelpDescription)}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className={cssExports.supportTeamBannerDivider} />

        {/* Middle: Team name, avatars, link */}
        <div className={cssExports.supportTeamBannerMiddle}>
          <div className={cssExports.supportTeamBannerTeamName}>
            {t("teamPageTitle", TranslationKeys.Common.YourEconomyTeam)}
          </div>
          <div className={cssExports.supportTeamBannerAvatars}>
            {team.members.slice(0, 3).map((member) => (
              <div key={member.imageUrl} className={cssExports.supportTeamBannerAvatar}>
                {member.imageUrl ? (
                  <img src={member.imageUrl} alt={member.name} />
                ) : (
                  <span>{member.name.charAt(0)}</span>
                )}
              </div>
            ))}
          </div>
          <ActionLinkButton
            className={clsx(cssExports.supportTeamBannerTeamLink, cssExports.seeMoreLink, cssExports.animatedLink)}
            onClick={onTeamClick}
            endIcon={<Icon icon={IconDefinitionConstants.WArrowSmallRight} displayType={DisplayType.Info} />}
          >
            {t("meetTheTeamLink", TranslationKeys.Common.MeetTheTeam)}
          </ActionLinkButton>
        </div>
      </div>

      {/* Right: Action cards */}
      <div className={cssExports.supportTeamBannerRight}>
        <ButtonBase component="div" onClick={onFAQClick}>
          <div className={cssExports.supportTeamActionCard}>
            <div className={cssExports.supportTeamActionIcon}>
              <Icon icon={IconDefinitionConstants.WQuestionCircle} displayType={DisplayType.Info} />
            </div>
            <div className={cssExports.supportTeamActionContent}>
              <div className={cssExports.supportTeamActionTitle}>
                {t("faqTitle", TranslationKeys.Common.Faq)}
              </div>
              <div className={cssExports.supportTeamActionSubtitle}>
                {t("faqSubtitle", TranslationKeys.Common.FaqSubtitle)}
              </div>
            </div>
          </div>
        </ButtonBase>

        <ButtonBase component="div" onClick={onSendMessageClick}>
          <div className={cssExports.supportTeamActionCard}>
            <div className={cssExports.supportTeamActionIcon}>
              <Icon icon={IconDefinitionConstants.WCommentDots} displayType={DisplayType.Info} />
            </div>
            <div className={cssExports.supportTeamActionContent}>
              <div className={cssExports.supportTeamActionTitle}>
                {t("sendMessageTitle", TranslationKeys.Common.SendMessage)}
              </div>
              <div className={cssExports.supportTeamActionSubtitle}>
                {t("sendMessageSubtitle", TranslationKeys.Common.SendMessageSubtitle)}
              </div>
            </div>
          </div>
        </ButtonBase>

        {showPhone && (
          <div className={cssExports.supportTeamPhoneLink}>
            <div className={cssExports.supportTeamActionCard}>
              <div className={cssExports.supportTeamActionIcon}>
                <Icon icon={IconDefinitionConstants.WSupportCenter} displayType={DisplayType.Info} />
              </div>
              <div className={cssExports.supportTeamActionContent}>
                <div className={cssExports.supportTeamActionTitle}>
                  {t("callSupportTitle", TranslationKeys.Common.CallSupport)}
                </div>
                <div className={cssExports.supportTeamActionSubtitle}>
                  {t("callSupportSubtitle", TranslationKeys.Common.CallSupportSubtitle)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTeamBanner;
